import React from "react";
import styled from "styled-components/macro";
import { Link } from "react-router-dom";
import { SocialIcons } from "../../components/SocialIcons/SocialIcons";
import { ViewHeader } from "../../components/ViewStyle/ViewStyle";
import { useStyleMediaQuery } from "../../hooks/useStyleMediaQuery";
import { Box, Button, Stack, VStack, HStack } from "@chakra-ui/react";

interface FeatureProps {
  title: string;
  feature: string;
}

export const Feature = styled(Link)`
  text-decoration: none;
  display: flex;
  flex-direction: column;
`;

export const ExternalFeature = styled.a`
  text-decoration: none;
  display: flex;
  flex-direction: column;
`;

export default function SocialBar({
  manifestData,
  viewData,
  anchorID,
  callFrom,
}: Record<string, any>): JSX.Element {
  const { matches: isMobile } = useStyleMediaQuery({
    mixOrMax: "max",
    widthOrHeight: "width",
    value: 768,
  });

  const config = viewData.config;
  const featuresList = manifestData?.["features"];
  let backgroundImageConfig = undefined;

  if (config && config.rgba1 && config.rgba2 && config.backgroundImageURL)
    backgroundImageConfig = `linear-gradient(${config.rgba1}, ${config.rgba2}), url(${config.backgroundImageURL})`;
  else if (config && config.rgba1 && config.rgba2)
    backgroundImageConfig = `linear-gradient(${config.rgba1}, ${config.rgba2})`;
  else if (config && config.backgroundImageURL)
    backgroundImageConfig = `url(${config.backgroundImageURL})`;

  return (
    <>
      {callFrom != "newsDigest" && viewData.showHorizontal && (
        <hr
          data-testid={"Horizontal"}
          style={{
            borderTop: "5px",
            borderStyle: "solid",
            borderTopColor:
              config && config.horizontalColor
                ? config.horizontalColor
                : "#eee",
          }}
        />
      )}

      <section
        id={anchorID}
        data-testid={"socialBarSection"}
        style={{
          background:
            config && config.backgroundColor ? config.backgroundColor : "#eee",
          backgroundImage: backgroundImageConfig,
          width: "100%",
          justifyContent: "center",
          alignItems: "center",
          backgroundSize: "cover",
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          padding: "1rem",
        }}
      >
        {isMobile ? (
          <VStack>
            {callFrom === "newsDigest" && viewData.title2 != "" ? (
              <ViewHeader
                titleColor={
                  config && config.titleColor ? config.titleColor : "black"
                }
                style={{
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {viewData.title2}
              </ViewHeader>
            ) : (
              <ViewHeader
                titleColor={
                  config && config.titleColor ? config.titleColor : "black"
                }
                style={{
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {viewData.title}
              </ViewHeader>
            )}
            <HStack>
              {callFrom === "newsDigest"
                ? viewData.socials.map((item: any) => (
                    <a
                      key={`socialBarNewsDigest` + item.icon}
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {SocialIcons(item.icon, "45px")}
                    </a>
                  ))
                : viewData.socialOn &&
                  viewData.socials.map((item: any) => (
                    <a
                      key={`socialBar` + item.icon}
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {SocialIcons(item.icon, "45px")}
                    </a>
                  ))}
            </HStack>
            <VStack>
              {callFrom != "newsDigest" &&
                viewData.buttonsOn &&
                viewData.buttons.map(
                  ({ feature, title }: FeatureProps, item: number) => (
                    <>
                      {featuresList[feature]?.external === true ? (
                        // for features with external links
                        <ExternalFeature
                          href={featuresList[feature].url}
                          target="_blank"
                          rel="noopener noreferrer"
                          key={title + item}
                        >
                          <Button
                            mb={3}
                            size="lg"
                            bg={config.btnColor}
                            color={config.btnTextColor}
                            style={{
                              textTransform: "uppercase",
                            }}
                          >
                            {title}
                          </Button>
                        </ExternalFeature>
                      ) : (
                        // for features reamining within the site
                        <Feature
                          key={title + item}
                          to={feature}
                        >
                          <Button
                            mb={3}
                            size="lg"
                            bg={config.btnColor}
                            color={config.btnTextColor}
                            style={{
                              textTransform: "uppercase",
                            }}
                          >
                            {title}
                          </Button>
                        </Feature>
                      )}
                    </>
                  )
                )}
            </VStack>
          </VStack>
        ) : (
          <Box
            p={5}
            justifyContent="center"
            display={{ sm: "wrap", lg: "flex" }}
          >
            <Box
              flexShrink={0}
              pr={5}
              pl={5}
              justifyContent="center"
              alignItems="center"
            >
              {callFrom === "newsDigest" && viewData.title2 != "" ? (
                <ViewHeader
                  titleColor={
                    config && config.titleColor ? config.titleColor : "black"
                  }
                  style={{
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {viewData.title2}
                </ViewHeader>
              ) : (
                <ViewHeader
                  titleColor={
                    config && config.titleColor ? config.titleColor : "black"
                  }
                  style={{
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {viewData.title}
                </ViewHeader>
              )}
            </Box>

            <Box
              justifyContent="center"
              alignContent="center"
              pr={5}
              pl={5}
            >
              <Stack
                direction={["column", "row"]}
                justify="center"
              >
                {callFrom === "newsDigest"
                  ? viewData.socials.map((item: any) => (
                      <a
                        key={`socialBar` + item.icon}
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {SocialIcons(item.icon, "45px")}
                      </a>
                    ))
                  : viewData.socialOn &&
                    viewData.socials.map((item: any) => (
                      <a
                        key={`socialBar` + item.icon}
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {SocialIcons(item.icon, "45px")}
                      </a>
                    ))}
              </Stack>
            </Box>

            <Box
              justifyContent="center"
              alignContent="center"
              pr={5}
              pl={5}
            >
              <Stack
                direction={["column", "row"]}
                justify="center"
                align="center"
              >
                {callFrom != "newsDigest" &&
                  viewData.buttonsOn &&
                  viewData.buttons.map(
                    ({ feature, title }: FeatureProps, item: number) => (
                      <>
                        {featuresList[feature]?.external === true ? (
                          // for features with external links
                          <ExternalFeature
                            href={featuresList[feature].url}
                            target="_blank"
                            rel="noopener noreferrer"
                            key={title + item}
                          >
                            <Button
                              mb={3}
                              size="lg"
                              bg={config.btnColor}
                              color={config.btnTextColor}
                              style={{
                                textTransform: "uppercase",
                              }}
                            >
                              {title}
                            </Button>
                          </ExternalFeature>
                        ) : (
                          // for features reamining within the site
                          <Feature
                            key={title + item}
                            to={feature}
                          >
                            <Button
                              mb={3}
                              size="lg"
                              bg={config.btnColor}
                              color={config.btnTextColor}
                              style={{
                                textTransform: "uppercase",
                              }}
                            >
                              {title}
                            </Button>
                          </Feature>
                        )}
                      </>
                    )
                  )}
              </Stack>
            </Box>
          </Box>
        )}
      </section>
      {callFrom != "newsDigest" && viewData.showHorizontal && (
        <hr
          data-testid={"Horizontal"}
          style={{
            borderTop: "5px",
            borderStyle: "solid",
            borderTopColor:
              config && config.horizontalColor
                ? config.horizontalColor
                : "#eee",
          }}
        />
      )}
    </>
  );
}
